import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';

import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentAudioRecord, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import { Box, Button, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ComponentLabel } from './ComponentLabel';
import { ContextualBar } from './ContextualBar';
import { useRightClickMenu } from './hooks/useRightClickMenu';
import { RightClickMenu } from './RightClickMenu';

import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useDebounce } from 'rooks';
import { useSlideComponent } from './hooks/useSlideComponent';
import { SlideRef } from './Slide';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentAudioRecord: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { deleteComponent, updateComponent, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const [aspectRatio] = useState(model.size.width / model.size.height);

    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentAudioRecord(model)) {
        return null;
    }

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`audioRecord-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
        >
            <ResizeBorder
                minSize={{ width: 214, height: 168 }}
                maxSize={{ width: 642, height: 504 }}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={aspectRatio}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#audioRecord-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.audioRecord.title')}
                    </ComponentLabel>
                )}

                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            height: 96,
                            width: 96,
                            borderRadius: 48,
                            backgroundColor: '#00B912',
                            border: '4px solid white',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <MicIcon
                            sx={{
                                height: 64,
                                width: 64,
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        height: '20%',
                        position: 'absolute',
                        bottom: 0,
                    }}
                >
                    <Button
                        component='label'
                        variant='outlined'
                        fullWidth
                        startIcon={<MicIcon sx={{ color: (theme) => (theme.vars || theme).palette.grey[300] }} />}
                        sx={(theme) => ({
                            height: '100%',
                            '&.Mui-disabled': {
                                backgroundColor: (theme.vars || theme).palette.grey[100],
                                borderColor: (theme) => (theme.vars || theme).palette.grey[300],
                            },
                        })}
                        disabled
                    >
                        <Typography
                            variant='h5'
                            sx={{
                                color: (theme) => (theme.vars || theme).palette.grey[300],
                            }}
                            noWrap
                        >
                            {t('slide.components.audioRecord.recordAgain')}
                        </Typography>
                    </Button>
                </Box>
            </ResizeBorder>

            {!preview && isEditing && (
                <ContextualBar
                    startButtons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: t('slide.components.audioRecord.tooltip.remove'),
                            onClick: () => {
                                onDeleteComponent?.(model.id);
                            },
                        },
                    ]}
                />
            )}

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
